import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Loader2, ChevronLeft, ChevronRight } from 'lucide-react'
import { api } from '@/lib/api'
import { useAuth } from '@/hooks/use-auth'
import { buildAppHref } from '@/lib/auth'
import { formatEpoch, entityRefClass } from '@/lib/format'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

function formatMacFromNumber(num: number): string {
  const hex = num.toString(16).toUpperCase().padStart(12, '0')
  return hex.match(/.{1,2}/g)!.join(':')
}

export function AdminSightingsPage() {
  const auth = useAuth()
  const [page, setPage] = useState(1)
  const [pageSize] = useState(50)
  const [deviceIdFilter, setDeviceIdFilter] = useState('')
  const [startTimestamp, setStartTimestamp] = useState('')
  const [endTimestamp, setEndTimestamp] = useState('')

  const buildParams = () => {
    const params: Record<string, string> = { page: String(page), page_size: String(pageSize) }
    if (deviceIdFilter) params.device_id = deviceIdFilter
    if (startTimestamp) params.start_timestamp = startTimestamp
    if (endTimestamp) params.end_timestamp = endTimestamp
    return params
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-sightings', page, pageSize, deviceIdFilter, startTimestamp, endTimestamp],
    queryFn: () => api.adminListSightings(buildParams()),
  })

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0

  return (
    <Card className="bg-card/95 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="text-2xl">Sightings</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div>
            <Label className="mb-1 block text-xs text-muted-foreground">Device ID</Label>
            <Input
              type="number"
              placeholder="Filter by device ID"
              className="w-44"
              value={deviceIdFilter}
              onChange={(e) => { setDeviceIdFilter(e.target.value); setPage(1) }}
            />
          </div>
          <div>
            <Label className="mb-1 block text-xs text-muted-foreground">Start Timestamp</Label>
            <Input
              type="number"
              placeholder="From"
              className="w-44"
              value={startTimestamp}
              onChange={(e) => { setStartTimestamp(e.target.value); setPage(1) }}
            />
          </div>
          <div>
            <Label className="mb-1 block text-xs text-muted-foreground">End Timestamp</Label>
            <Input
              type="number"
              placeholder="To"
              className="w-44"
              value={endTimestamp}
              onChange={(e) => { setEndTimestamp(e.target.value); setPage(1) }}
            />
          </div>
        </div>

        {isLoading && (
          <div className="py-12 text-center text-muted-foreground">
            <Loader2 className="inline h-5 w-5 animate-spin mr-2" /> Loading sightings...
          </div>
        )}

        {error && (
          <div className="rounded-lg border-l-4 border-destructive bg-destructive/10 p-4 text-destructive">
            Error loading sightings: {(error as Error).message}
          </div>
        )}

        {data && (
          <>
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-primary hover:bg-primary">
                    <TableHead className="text-primary-foreground font-semibold">Device ID</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Mac Address</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Depart</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Arrive</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground">No sightings found</TableCell>
                    </TableRow>
                  ) : (
                    data.items.map((s, i) => (
                      <TableRow key={`${s.device_id}-${s.sighting_mac_address}-${s.sighting_depart}-${i}`}>
                        <TableCell className="font-medium">
                          <Link to={buildAppHref(`/admin/devices/${s.device_id}`, auth)} className={entityRefClass}>{s.device_id}</Link>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{formatMacFromNumber(s.sighting_mac_address)}</TableCell>
                        <TableCell>{formatEpoch(s.sighting_depart)}</TableCell>
                        <TableCell>{formatEpoch(s.sighting_arrive)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Page {data.page} of {totalPages} ({data.total} total)
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
